import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Mail, RefreshCw, Download, Trash2, ChevronLeft, ChevronRight,
  ArrowUp, ArrowDown, ArrowUpDown, X, Eye,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import * as waitlistApi from '../../api/waitlist.api';
import { errorMessage } from '../../api/client';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';

// Labels for the Early Access questionnaire keys stored by the backend
// (backend/src/models/WaitlistSignup.js).
const AGE_LABELS = {
  under_25: 'Under 25', '25_34': '25–34', '35_44': '35–44', '45_54': '45–54', '55_plus': '55+',
};
const INTEREST_LABELS = {
  general_health_check: 'General health check',
  diabetes_blood_sugar: 'Diabetes / blood sugar',
  cholesterol_heart: 'Cholesterol / heart health',
  liver: 'Liver health',
  kidney: 'Kidney health',
  vitamins_nutrition: 'Vitamins & nutrition',
  weight_management: 'Weight management',
  overall_health: 'Overall health',
  other: 'Other',
};
const TIMING_LABELS = {
  asap: 'As soon as available',
  '1_3_months': 'Within 1–3 months',
  '3_6_months': 'Within 3–6 months',
  exploring: 'Just exploring',
};
const TIMING_TONE = { asap: 'success', '1_3_months': 'brand', '3_6_months': 'info', exploring: 'neutral' };

let regionNames = null;
try { regionNames = new Intl.DisplayNames(['en'], { type: 'region' }); } catch { /* old browser */ }

// Rows from before the global form (2026-10-08) have no country — they all
// came from the Malaysia-only form.
function countryIso(r) {
  return r.country || 'MY';
}
function countryLabel(iso) {
  try { return regionNames?.of(iso) || iso; } catch { return iso; }
}
function flagEmoji(iso) {
  return String(iso).toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));
}

const SOURCE_LABELS = {
  preventive_health: 'Website (EN)',
  preventive_health_cn: 'Website (CN)',
  imported: 'Imported list',
  other: 'Other',
};
const sourceLabel = (s) => SOURCE_LABELS[s] || s;
// Imported contacts may have no email — fall back to name / phone.
const rowLabel = (r) => r.firstName || r.email || r.phone || 'this entry';

const EMPTY_FILTERS = {
  search: '', source: '', list: '', country: '', ageGroup: '', interest: '', testTiming: '', consent: '', from: '', to: '',
};

function waLink(phone) {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : null;
}

// Admin / super-admin view of Early Access registrations from the public
// /preventive-health pages. Only super-admin can delete.
function Waitlist() {
  const { user } = useAuth();
  const canDelete = user?.role === ROLES.SUPER_ADMIN;

  const [data, setData] = useState({ items: [], total: 0, totalPages: 1, sources: [] });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState({ sort: 'createdAt', dir: 'desc' });
  const [paging, setPaging] = useState({ page: 1, pageSize: 50 });
  const [selected, setSelected] = useState(null);

  const params = useCallback(() => {
    const p = { ...sort };
    Object.entries(filters).forEach(([k, v]) => { if (v !== '' && v != null) p[k] = v; });
    return p;
  }, [filters, sort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fresh = await waitlistApi.listWaitlist({ ...params(), ...paging });
      setData(fresh || { items: [] });
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [params, paging]);

  useEffect(() => { load(); }, [load]);

  function setFilter(k, v) {
    setFilters((s) => ({ ...s, [k]: v }));
    setPaging((p) => ({ ...p, page: 1 }));
  }
  function clearFilters() {
    setFilters(EMPTY_FILTERS);
    setPaging((p) => ({ ...p, page: 1 }));
  }
  function toggleSort(field) {
    setSort((s) => ({
      sort: field,
      dir: s.sort === field ? (s.dir === 'asc' ? 'desc' : 'asc') : field === 'createdAt' ? 'desc' : 'asc',
    }));
    setPaging((p) => ({ ...p, page: 1 }));
  }
  function gotoPage(delta) {
    setPaging((p) => ({ ...p, page: Math.max(1, Math.min(data.totalPages, p.page + delta)) }));
  }

  async function onExport() {
    try {
      const blob = await waitlistApi.exportWaitlistCsv(params());
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `early-access-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  async function onDelete(id, label) {
    if (!window.confirm(`Delete waitlist entry for ${label}? This cannot be undone.`)) return;
    try {
      await waitlistApi.deleteWaitlistEntry(id);
      toast.success('Deleted');
      setSelected(null);
      load();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }

  const hasFilters = Object.values(filters).some((v) => v !== '');

  function SortTh({ field, children }) {
    const active = sort.sort === field;
    const Icon = !active ? ArrowUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th
        onClick={() => toggleSort(field)}
        style={{ cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' }}
        aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        {children}{' '}
        <Icon size={12} style={{ verticalAlign: 'middle', opacity: active ? 1 : 0.4 }} />
      </th>
    );
  }

  return (
    <>
      <PageHeader
        title="Early Access signups"
        subtitle="Registrations and questionnaire answers from the public /preventive-health pages."
        actions={
          <div className="row" style={{ gap: 8 }}>
            <button className="btn btn-outline" onClick={onExport} disabled={loading || !data.total}>
              <Download size={14} /> Export CSV
            </button>
            <button className="btn btn-outline" onClick={load} disabled={loading}>
              <RefreshCw size={14} /> Refresh
            </button>
          </div>
        }
      />

      <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
        <div className="grid grid-4" style={{ gap: 'var(--s-3)' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Search</label>
            <input
              type="text"
              className="input"
              placeholder="Name, email, phone or postcode"
              value={filters.search}
              onChange={(e) => setFilter('search', e.target.value)}
            />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Country</label>
            <select className="input" value={filters.country} onChange={(e) => setFilter('country', e.target.value)}>
              <option value="">All countries</option>
              {(data.countries || [])
                .map((iso) => ({ iso, name: countryLabel(iso) }))
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((c) => <option key={c.iso} value={c.iso}>{flagEmoji(c.iso)} {c.name}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Age group</label>
            <select className="input" value={filters.ageGroup} onChange={(e) => setFilter('ageGroup', e.target.value)}>
              <option value="">All ages</option>
              {Object.entries(AGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              <option value="none">Not answered</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Area of interest</label>
            <select className="input" value={filters.interest} onChange={(e) => setFilter('interest', e.target.value)}>
              <option value="">All interests</option>
              {Object.entries(INTEREST_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Blood test timing</label>
            <select className="input" value={filters.testTiming} onChange={(e) => setFilter('testTiming', e.target.value)}>
              <option value="">Any timing</option>
              {Object.entries(TIMING_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              <option value="none">Not answered</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Consent to updates</label>
            <select className="input" value={filters.consent} onChange={(e) => setFilter('consent', e.target.value)}>
              <option value="">Any</option>
              <option value="yes">Consented</option>
              <option value="no">Not consented</option>
            </select>
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Source</label>
            <select className="input" value={filters.source} onChange={(e) => setFilter('source', e.target.value)}>
              <option value="">All sources</option>
              {(data.sources || []).map((s) => <option key={s} value={s}>{sourceLabel(s)}</option>)}
            </select>
          </div>
          {(data.lists || []).length > 0 && (
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Imported list</label>
              <select className="input" value={filters.list} onChange={(e) => setFilter('list', e.target.value)}>
                <option value="">All lists</option>
                {data.lists.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
          )}
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Joined from</label>
            <input type="date" className="input" value={filters.from} onChange={(e) => setFilter('from', e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>Joined to</label>
            <input type="date" className="input" value={filters.to} onChange={(e) => setFilter('to', e.target.value)} />
          </div>
        </div>
        <div className="row between" style={{ marginTop: 'var(--s-3)', alignItems: 'center' }}>
          <span className="muted text-sm">
            {data.total ? `${data.total.toLocaleString()} entries` : '—'}
          </span>
          {hasFilters && (
            <button className="btn btn-link" onClick={clearFilters}>
              <X size={14} /> Clear filters
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : !data.items.length ? (
        <EmptyState
          icon={Mail}
          title={hasFilters ? 'No signups match these filters' : 'No Early Access signups yet'}
          description="Registrations submitted on the /preventive-health pages will show up here."
        />
      ) : (
        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <SortTh field="firstName">Name</SortTh>
                <SortTh field="email">Email</SortTh>
                <th>Phone</th>
                <SortTh field="country">Country</SortTh>
                <SortTh field="postcode">Zip / Postcode</SortTh>
                <SortTh field="ageGroup">Age</SortTh>
                <th>Interests</th>
                <SortTh field="testTiming">Blood test</SortTh>
                <th>Consent</th>
                <SortTh field="source">Source</SortTh>
                <SortTh field="createdAt">Joined</SortTh>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((r) => (
                <tr key={r._id}>
                  <td>{r.firstName || <span className="muted">—</span>}</td>
                  <td>{r.email || <span className="muted">—</span>}</td>
                  <td className="text-xs" style={{ whiteSpace: 'nowrap' }}>
                    {r.phone ? (
                      <a href={waLink(r.phone)} target="_blank" rel="noopener noreferrer">{r.phone}</a>
                    ) : <span className="muted">—</span>}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }} title={countryLabel(countryIso(r))}>
                    {flagEmoji(countryIso(r))} {countryLabel(countryIso(r))}
                  </td>
                  <td>{r.postcode || <span className="muted">—</span>}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{AGE_LABELS[r.ageGroup] || <span className="muted">—</span>}</td>
                  <td className="text-xs" style={{ maxWidth: 240 }}>
                    {(r.interests || []).length
                      ? r.interests.map((i) => INTEREST_LABELS[i] || i).join(', ')
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    {r.testTiming
                      ? <span className={`badge ${TIMING_TONE[r.testTiming] || ''}`}>{TIMING_LABELS[r.testTiming]}</span>
                      : <span className="muted">—</span>}
                  </td>
                  <td>
                    <span className={`badge ${r.marketingConsent ? 'success' : 'neutral'}`}>
                      {r.marketingConsent ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}><span className={`badge ${r.source === 'imported' ? 'info' : ''}`}>{sourceLabel(r.source)}</span>
                    {r.importList && <div className="muted text-xs" style={{ marginTop: 2 }}>{r.importList}</div>}
                  </td>
                  <td className="cell-mono text-xs" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(r.createdAt).toLocaleString()}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <button className="btn btn-link" onClick={() => setSelected(r)} title="View details">
                      <Eye size={14} />
                    </button>
                    {canDelete && (
                      <button
                        className="btn btn-link"
                        onClick={() => onDelete(r._id, rowLabel(r))}
                        title="Delete entry"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="row between" style={{ marginTop: 'var(--s-3)', alignItems: 'center' }}>
            <div className="muted text-sm">
              Page {data.page || paging.page} of {data.totalPages || 1}
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button
                className="btn btn-outline"
                onClick={() => gotoPage(-1)}
                disabled={(data.page || 1) <= 1}
              >
                <ChevronLeft size={14} /> Prev
              </button>
              <button
                className="btn btn-outline"
                onClick={() => gotoPage(+1)}
                disabled={(data.page || 1) >= (data.totalPages || 1)}
              >
                Next <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      {selected && (
        <SignupDetails
          row={selected}
          canDelete={canDelete}
          onDelete={() => onDelete(selected._id, rowLabel(selected))}
          onClose={() => setSelected(null)}
        />
      )}
    </>
  );
}

function SignupDetails({ row, canDelete, onDelete, onClose }) {
  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const fields = [
    ['First name', row.firstName],
    ['Email', row.email && <a href={`mailto:${row.email}`}>{row.email}</a>],
    ['Phone (WhatsApp)', row.phone && <a href={waLink(row.phone)} target="_blank" rel="noopener noreferrer">{row.phone}</a>],
    ['Phone country code', row.phone && `${flagEmoji(row.phoneCountry || 'MY')} ${countryLabel(row.phoneCountry || 'MY')}`],
    ['Country', `${flagEmoji(countryIso(row))} ${countryLabel(countryIso(row))}`],
    ['Zip / Postcode', row.postcode],
    ['Age group', AGE_LABELS[row.ageGroup]],
    ['Interests', (row.interests || []).map((i) => INTEREST_LABELS[i] || i).join(', ')],
    ['Other interest', row.interestOther],
    ['Blood test timing', TIMING_LABELS[row.testTiming]],
    ['Consent to updates', row.marketingConsent
      ? `Yes${row.consentAt ? ` (${new Date(row.consentAt).toLocaleString()})` : ''}`
      : 'No'],
    ['Source', sourceLabel(row.source)],
    ['Imported list', row.importList],
    ['Joined', new Date(row.createdAt).toLocaleString()],
    ['Last updated', row.updatedAt && new Date(row.updatedAt).toLocaleString()],
    ['IP address', row.ipAddress],
  ];

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal" role="dialog" aria-modal="true" style={{ maxWidth: 520 }}>
        <div className="modal-head"><h3>{rowLabel(row)}</h3></div>
        <div className="modal-body">
          <dl style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: '8px 12px', margin: 0 }}>
            {fields.map(([label, value]) => (
              <React.Fragment key={label}>
                <dt className="muted text-sm">{label}</dt>
                <dd className="text-sm" style={{ margin: 0, color: 'var(--text)', wordBreak: 'break-word' }}>
                  {value || <span className="muted">—</span>}
                </dd>
              </React.Fragment>
            ))}
          </dl>
        </div>
        <div className="modal-foot">
          {canDelete && (
            <button className="btn btn-danger" onClick={onDelete}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default Waitlist;
